import { useEffect, useMemo, useState } from 'react';
import { backendUrl } from '../utils/AxiosConfiguration';
import Loading from '../pages/Loading';
import NotFound from '../pages/NotFound';
import Welcome from '../pages/Welcome';
import { ClassicTemplate } from './ClassicTemplate';
import { TemplateErrorBoundary } from './TemplateErrorBoundary';
import {
  SUPPORTED_CONTRACT_VERSIONS,
  ViewPayload,
  VcardContextData,
  TemplateComponent,
  LandingViewComponent,
  ErrorViewComponent,
  TemplateProps,
  LandingProps,
  ErrorProps,
} from './contract';

const LOAD_TIMEOUT_MS = 10_000;

function absolute(path: string): string {
  return `${backendUrl}${path}`;
}

const defaultImporter = (url: string) => import(/* @vite-ignore */ url);

interface TemplateLoaderProps {
  payload: ViewPayload;
  view?: 'vcard' | 'error' | 'landing';
  importer?: (url: string) => Promise<unknown>;
}

type AnyViewComponent = TemplateComponent | LandingViewComponent | ErrorViewComponent;

type LoadState =
  | { kind: 'loading' }
  | { kind: 'fallback' }
  | { kind: 'custom'; Comp: AnyViewComponent };

export function TemplateLoader({ payload, view = 'vcard', importer = defaultImporter }: TemplateLoaderProps) {
  const tpl = payload.template;
  const usable = !!tpl && SUPPORTED_CONTRACT_VERSIONS.includes(tpl.contractVersion);
  const [state, setState] = useState<LoadState>(usable ? { kind: 'loading' } : { kind: 'fallback' });

  const genericFallback = () => {
    if (view === 'error') return <NotFound brand={payload.context.brand} />;
    if (view === 'landing') return <Welcome brand={payload.context.brand} />;
    return <ClassicTemplate data={payload.context as VcardContextData} asset={() => ''} />;
  };

  useEffect(() => {
    if (!usable || !tpl) {
      setState({ kind: 'fallback' });
      return;
    }
    setState({ kind: 'loading' });
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    let styleLink: HTMLLinkElement | null = null;
    if (tpl.stylesUrl) {
      styleLink = document.createElement('link');
      styleLink.rel = 'stylesheet';
      styleLink.href = absolute(tpl.stylesUrl);
      document.head.appendChild(styleLink);
    }

    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('Template-Load-Timeout')), LOAD_TIMEOUT_MS);
    });
    Promise.race([importer(absolute(tpl.bundleUrl)), timeout])
      .then((mod) => {
        if (cancelled) return;
        const modRecord = mod as Record<string, unknown>;
        const pick = view === 'error' ? modRecord.ErrorView : view === 'landing' ? modRecord.LandingView : modRecord.default;
        if (typeof pick === 'function') {
          setState({ kind: 'custom', Comp: pick as AnyViewComponent });
        } else {
          setState({ kind: 'fallback' });
        }
      })
      .catch((error) => {
        console.error('Template-Bundle konnte nicht geladen werden, falle auf Fallback zurück:', error);
        if (!cancelled) setState({ kind: 'fallback' });
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      styleLink?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpl?.id, usable, view]);

  const asset = useMemo(() => {
    if (!tpl) return () => '';
    const suffix = tpl.preview ? `?preview=${encodeURIComponent(tpl.preview)}` : '';
    return (name: string) => {
      const full = name.startsWith('assets/') ? name : 'assets/' + name;
      return `${absolute(tpl.assetBaseUrl)}${encodeURIComponent(full)}${suffix}`;
    };
  }, [tpl]);

  if (state.kind === 'loading') return <Loading />;
  if (state.kind === 'fallback') return genericFallback();

  if (view === 'error') {
    const errorProps: ErrorProps = {
      data: payload.context,
      asset,
      error: { kind: 'notFound', urlKey: payload.kind === 'notFound' ? payload.urlKey : undefined },
    };
    const ErrComp = state.Comp as ErrorViewComponent;
    return (
      <TemplateErrorBoundary fallback={genericFallback()}>
        <ErrComp {...errorProps} />
      </TemplateErrorBoundary>
    );
  }
  if (view === 'landing') {
    const landingProps: LandingProps = { data: payload.context, asset };
    const LandComp = state.Comp as LandingViewComponent;
    return (
      <TemplateErrorBoundary fallback={genericFallback()}>
        <LandComp {...landingProps} />
      </TemplateErrorBoundary>
    );
  }
  const vcardProps: TemplateProps = { data: payload.context as VcardContextData, asset };
  const VcardComp = state.Comp as TemplateComponent;
  return (
    <TemplateErrorBoundary fallback={genericFallback()}>
      <VcardComp {...vcardProps} />
    </TemplateErrorBoundary>
  );
}
